import React, { useState } from 'react';
import { GestureResponderEvent, TouchableWithoutFeedback } from 'react-native';
import className from 'classnames';
import './Toaster.scss';

import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  Stack, HeaderText, BodyText,
} from '..';

import { useMount, useResponsiveEffect } from '../../../hooks';
import { NAV_BAR_HEIGHT } from '../../shared/BottomNavBar/BottomNavBar';

const DEFAULT_TIMEOUT = 10000;

/**
 * Basic Toaster
 * See storybook.
 *
 */

export type ToasterProps = {
  boxColor?: string,
  content: string,
  dismissOnPress?: boolean;
  icon?: React.ReactNode,
  idleTimeout?: number,
  isLoggedIn?: boolean,
  onDismiss?: () => void,
  onPress?: () => void,
  testID?: string;
  title: string,
};

const Toaster: React.FC<ToasterProps> = ({
  boxColor = colors.green,
  content,
  dismissOnPress = false,
  icon = null,
  idleTimeout = DEFAULT_TIMEOUT,
  isLoggedIn = false,
  onDismiss = () => {},
  onPress = () => {},
  testID = 'toaster',
  title,
}) => {
  const [show, setShow] = useState(true);
  const is = useResponsiveEffect();

  const dismiss = () => {
    setShow(false);
    onDismiss();
  };

  let style = {
    bottom: 50,
    left: 16,
  };

  if (is.mobile || is.tablet) {
    const bottom = isLoggedIn ? 20 + NAV_BAR_HEIGHT : 20;
    style = {
      bottom,
      left: 16,
    };
  }

  useMount(() => {
    setTimeout(() => {
      dismiss();
    }, idleTimeout);
  });

  const handlePress = (e: GestureResponderEvent) => {
    // Prevent event from falling through the fixed position element
    e.preventDefault();
    e.stopPropagation();

    onPress();

    if (dismissOnPress) {
      dismiss();
    }
  };

  if (!show) {
    return null;
  }

  return (
    <TouchableWithoutFeedback onPress={handlePress}>
      <Inline
        color={colors.whiteWash}
        borderRadius={4}
        showBoxShadow
        space={12}
        overflow="hidden"
        paddingRight={16}
        testID={testID}
        style={style}
        className={className('toaster')}
      >

        <Box color={boxColor} minWidth={5} maxWidth={5} />
        <Box justifyContent="center">
          {icon}
        </Box>

        <Stack flexShrink={1} paddingY={24} alignItems="flex-end" space={12}>
          <Stack space={4}>
            <HeaderText level={4}>{title}</HeaderText>
            <BodyText appearance="smaller" color={colors.metal}>
              {content}
            </BodyText>
          </Stack>
        </Stack>
      </Inline>
    </TouchableWithoutFeedback>
  );
};

export default Toaster;
